import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, GlobeIcon } from 'lucide-react';
import { useLanguage, useTranslation } from '../i18n';
import type { Language } from '../i18n';
const DEMO_URL = 'https://calendar.app.google/kAzzsMuwfngdzNZf9';
export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const {
    language,
    setLanguage
  } = useLanguage();
  const {
    t
  } = useTranslation();
  const langRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  return <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#050505]/90 backdrop-blur-md py-4 border-b border-white/5' : 'bg-transparent py-6'}`} data-id="element-117">
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between" data-id="element-118">
        <div className="flex items-center" data-id="element-119">
          <Link to="/" data-id="element-120">
            <img src="https://cdn.magicpatterns.com/uploads/7iLxTjsS92ZrEik9BZ7uDm/TRIPPP.png" alt="Trippp Logo" className="h-8 object-contain" data-id="element-121" />
          </Link>
        </div>

        <div className="hidden md:flex items-center space-x-8" data-id="element-122">
          <div className="relative group" data-id="element-123">
            <button className="flex items-center text-sm font-medium text-white/80 hover:text-white transition-colors py-2" data-id="element-124">
              {t('navbar.products')}{' '}
              <ChevronDownIcon className="w-4 h-4 ml-1 opacity-50" data-id="element-125" />
            </button>
            <div className="absolute top-full left-0 mt-2 w-48 bg-[#0A0A0A] border border-white/10 rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 overflow-hidden" data-id="element-126">
              <Link to="/products/map" className="block px-4 py-3 text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors" data-id="element-127">
                {t('navbar.tripppMap')}
              </Link>
              <Link to="/products/spark" className="block px-4 py-3 text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors" data-id="element-128">
                {t('navbar.tripppSpark')}
              </Link>
              <Link to="/products/ops" className="block px-4 py-3 text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors" data-id="element-129">
                {t('navbar.tripppTripOps')}
              </Link>
            </div>
          </div>

          <div className="relative group" data-id="element-130">
            <button className="flex items-center text-sm font-medium text-white/80 hover:text-white transition-colors py-2" data-id="element-131">
              {t('navbar.useCases')}{' '}
              <ChevronDownIcon className="w-4 h-4 ml-1 opacity-50" data-id="element-132" />
            </button>
            <div className="absolute top-full left-0 mt-2 w-64 bg-[#0A0A0A] border border-white/10 rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 overflow-hidden" data-id="element-133">
              <Link to="/use-cases/dmo" className="block px-4 py-3 text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors" data-id="element-134">
                {t('navbar.dmosAndTourismBoards')}
              </Link>
              <Link to="/use-cases/dmc" className="block px-4 py-3 text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors" data-id="element-135">
                {t('navbar.dmcsAndTravelAgencies')}
              </Link>
              <Link to="/use-cases/hotels" className="block px-4 py-3 text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors" data-id="element-136">
                {t('navbar.hotelsAndResorts')}
              </Link>
              <Link to="/use-cases/real-estate" className="block px-4 py-3 text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors" data-id="element-137">
                {t('navbar.realEstateAndLifestyle')}
              </Link>
            </div>
          </div>

          <Link to="/pricing" className="text-sm font-medium text-white/80 hover:text-white transition-colors" data-id="element-138">
            {t('navbar.pricing')}
          </Link>
          <Link to="/about" className="text-sm font-medium text-white/80 hover:text-white transition-colors" data-id="element-139">
            {t('navbar.about')}
          </Link>
          <Link to="/contact" className="text-sm font-medium text-white/80 hover:text-white transition-colors" data-id="element-140">
            {t('navbar.contact')}
          </Link>
        </div>

        <div className="flex items-center gap-4" data-id="element-141">
          <a href={DEMO_URL} target="_blank" rel="noopener noreferrer" className="rounded-full border-gradient-animated px-6 py-2.5 text-sm font-semibold text-white inline-block" data-id="element-142">
            <span className="relative z-10" data-id="element-143">{t('navbar.bookDemo')}</span>
          </a>

          {/* Language dropdown */}
          <div className="relative" ref={langRef} data-id="element-144">
            <button type="button" onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 text-sm font-medium text-white/60 hover:text-white transition-colors py-2" data-id="element-145">
              <GlobeIcon className="w-4 h-4" data-id="element-146" />
              <span data-id="element-147">{language}</span>
              <ChevronDownIcon className={`w-3 h-3 transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`} data-id="element-148" />
            </button>
            {langOpen && <div className="absolute top-full right-0 mt-2 w-28 bg-[#0A0A0A] border border-white/10 rounded-xl shadow-xl overflow-hidden" data-id="element-149">
                {(['EN', 'FR', 'ES', 'PT'] as Language[]).map(lang => <button key={lang} type="button" onClick={() => {
              setLanguage(lang);
              setLangOpen(false);
            }} className={`block w-full text-left px-4 py-2.5 text-sm transition-colors ${language === lang ? 'text-white bg-white/10' : 'text-white/60 hover:text-white hover:bg-white/5'}`} data-id="element-150">
                    {lang}
                  </button>)}
              </div>}
          </div>
        </div>
      </div>
    </nav>;
}